import { cn } from '@invent/wl-ui-kit-next';
import { Button } from '@invent/wl-ui-kit-next/ui';

import { errorMessage } from '@/domains/api';
import type { IEnteredNumber } from '../storage';
import type { IRegistrationView } from '../types';
import { DOOR, enteredNumberText, registeredSinceText } from './registration-copy';
import { StepHeading, useStepAction } from './step-parts';

/**
 * "Is this you?" — a person's run's `confirm_identity`, by the number in hand: page 03, what
 * FINRA holds against the CRD (SR-487 decision 4), or page 06, what we hold against an insurance
 * number (decision 6) — and the wireframes' two answers.
 *
 * **Every row is drawn, an empty one as a dash**: a row that vanished would read as a field the
 * record does not have. The number's row is the number as entered, since the view does not carry
 * it. Page 03's five: Name — the registry's, or ours where FINRA named nobody and the number is
 * one we hold — CRD, and FINRA's Current firm, Firm CRD and Registered since (`registryFirm`,
 * `registeredSince`). Page 06 is drawn only where we hold the number, so its rows are our
 * participant's: Name, the NPN or licence, and the state of its profile (`officeState`); no
 * licence status or lines of authority, as there is no NIPR (PFR D8). The view's `sightings` are
 * not drawn — the wireframe has no place for them — and no third answer is offered (Q5): "No,
 * that's not me" is `confirmed: false`, which takes the run back to the same number.
 */
export function RecordStep({
  view,
  entered,
  onDecide
}: {
  view: IRegistrationView;
  entered: IEnteredNumber | undefined;
  onDecide: (confirmed: boolean) => Promise<void>;
}) {
  const { pending, error, run } = useStepAction();
  const failure = error === null ? null : errorMessage(error, 'Could not record your answer.');
  const record =
    view.number === 'insurance' ? ourRecord(view, entered) : finraRecord(view, entered);
  // Both pages' heading and answers are the same words.
  const words = DOOR.finraRecord;

  return (
    <>
      <StepHeading title={words.title} lede={record.lede} />

      <RecordBox rows={record.rows} source={record.source} />

      {failure && (
        <p role="alert" className="mb-4 text-sm font-medium text-destructive">
          {failure}
        </p>
      )}

      <div className="grid gap-3">
        <Button disabled={pending} onClick={() => void run(() => onDecide(true))}>
          {words.yes}
        </Button>
        <button
          type="button"
          disabled={pending}
          onClick={() => void run(() => onDecide(false))}
          className={cn(
            'justify-self-center text-sm text-[var(--color-sapphire)] underline-offset-4',
            'hover:underline disabled:opacity-60'
          )}
        >
          {words.no}
        </button>
      </div>
    </>
  );
}

type Row = [string, Nullable<string> | undefined];

/** Page 03's lede, rows and source. */
function finraRecord(view: IRegistrationView, entered: IEnteredNumber | undefined) {
  const words = DOOR.finraRecord;
  const rows: Row[] = [
    [words.rows.name, view.registryName ?? view.displayName],
    [words.rows.crd, entered?.value],
    [words.rows.firm, view.registryFirm?.name],
    [words.rows.firmCrd, view.registryFirm?.crd],
    [words.rows.registeredSince, registeredSinceText(view.registeredSince)]
  ];
  return { lede: words.lede(entered?.value ?? DOOR.empty), rows, source: words.source };
}

/** Page 06's: the number named by the type it was sent as — an NPN or a licence. */
function ourRecord(view: IRegistrationView, entered: IEnteredNumber | undefined) {
  const words = DOOR.ourRecord;
  const licensed = entered?.idType === 'INSURANCE_LICENSE';
  const rows: Row[] = [
    [words.rows.name, view.displayName ?? view.registryName],
    [licensed ? words.rows.license : words.rows.npn, entered?.value],
    [words.rows.state, view.officeState]
  ];
  const named = entered ? enteredNumberText(entered) : DOOR.empty;
  return { lede: words.lede(named), rows, source: words.source };
}

/** The record, a bordered box as the wireframes draw it: a term and its value per row, then
 *  where the record is from. */
function RecordBox({
  rows,
  source
}: {
  rows: readonly Row[];
  source: string;
}) {
  return (
    <div
      data-record
      className="mb-6 rounded-md border border-border bg-[var(--color-slight-blue)]/20 p-4"
    >
      <dl className="grid gap-1.5 text-sm">
        {rows.map(([term, value]) => (
          <div key={term} className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-3">
            <dt className="text-muted-foreground">{term}</dt>
            <dd className={cn('font-medium break-words', !value && 'text-muted-foreground')}>
              {value || DOOR.empty}
            </dd>
          </div>
        ))}
      </dl>
      <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
        <span aria-hidden="true" className="size-1.5 rounded-full bg-current" />
        {source}
      </p>
    </div>
  );
}
